import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { loadFavorites, loadAddfavorites } from "../constants/products";
import Swal from "sweetalert2";

let cachedFavorites = null;
let cachedUserId = null;

const FavoritesContext = createContext();

const getLoggedUserId = () => {
  const loggedUserData = JSON.parse(localStorage.getItem("user"));
  return loggedUserData?.data?.user?.id || null;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedUserId, setLoadedUserId] = useState(null);

  const ensureFavoritesLoaded = useCallback(async ({ force = false } = {}) => {
    const userId = getLoggedUserId();

    if (!userId) {
      cachedFavorites = null;
      cachedUserId = null;
      setFavorites([]);
      setLoadedUserId(null);
      return [];
    }

    if (!force && cachedUserId === userId && Array.isArray(cachedFavorites)) {
      setFavorites(cachedFavorites);
      setLoadedUserId(userId);
      return cachedFavorites;
    }

    setLoading(true);
    try {
      const data = await loadFavorites(userId);
      const nextFavorites = Array.isArray(data) ? data : [];

      cachedFavorites = nextFavorites;
      cachedUserId = userId;
      setFavorites(nextFavorites);
      setLoadedUserId(userId);

      return nextFavorites;
    } catch (error) {
      console.error("Error al cargar favoritos:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const favoriteCodes = useMemo(
    () => new Set(favorites.map((favorite) => favorite.codigo)),
    [favorites]
  );

  const isFavorite = useCallback(
    (codigo) => favoriteCodes.has(codigo),
    [favoriteCodes]
  );

  const toggleFavorite = async (product) => {
    const userId = getLoggedUserId();
    if (!userId) {
      Swal.fire("Debes iniciar sesion", "", "warning");
      return false;
    }

    const currentFavorites =
      loadedUserId === userId ? favorites : await ensureFavoritesLoaded();
    const isFav = currentFavorites.some((favorite) => favorite.codigo === product.codigo);
    const borrar = isFav ? 1 : 0;

    try {
      await loadAddfavorites(userId, product.codigo, borrar);

      const updated =
        borrar === 1
          ? currentFavorites.filter((favorite) => favorite.codigo !== product.codigo)
          : [...currentFavorites, { codigo: product.codigo }];

      cachedFavorites = updated;
      cachedUserId = userId;
      setFavorites(updated);
      setLoadedUserId(userId);

      Swal.fire(
        borrar === 1 ? "Eliminado de favoritos" : "Anadido a favoritos",
        "",
        "success"
      );

      return true;
    } catch (error) {
      console.error("Error al actualizar favoritos:", error);
      Swal.fire("Error", "No se pudo actualizar favoritos.", "error");
      return false;
    }
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        loading,
        ensureFavoritesLoaded,
        isFavorite,
        toggleFavorite,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
